import React from 'react';

import { Community } from '@lumapps/communities/types';
import { Content } from '@lumapps/contents/types';
import { NavigationItemType, NavigationMapPageReferenceType } from '@lumapps/navigation/types';

import { AddToMainNavigationDialog as UIAddToMainNavigationDialog } from './AddToMainNavigationDialog';
import { useAddToMainNavigationDialog } from './useAddToMainNavigationDialog';

export interface AddToNavigationBaseProps {
    type: NavigationMapPageReferenceType;
    entity: Content | Community;
}

export interface AddContentToNavigationProps {
    type: NavigationItemType.CONTENT;
    entity: Content;
}

export interface AddCommunityToNavigationProps {
    type: NavigationItemType.COMMUNITY;
    entity: Community;
}

export type AddToNavigationProps =
    | AddContentToNavigationProps
    | AddCommunityToNavigationProps
    | AddToNavigationBaseProps;

export type AddToMainNavigationDialogProps = AddToNavigationProps & {
    isOpen?: boolean;
    onClose?(): void;
};

/**
 * ListItem to add the given entity to the main navigation.
 *
 */
export const AddToMainNavigationDialog: React.FC<AddToMainNavigationDialogProps> = ({
    isOpen,
    onClose,
    entity,
    type,
}) => {
    const { handleOpen, onClose: dialogOnClose, ...dialogProps } = useAddToMainNavigationDialog({ entity, type });

    /**
     * The dialog needs to fetch some information (the main nav / content types) before opening.
     * It has its own inner "open" state management that we need to synchronize with the prop.
     *  */
    React.useEffect(() => {
        // If the parent request the dialog to open and it's not opened yet, trigger the open callback.
        if (isOpen && !dialogProps.isOpen) {
            handleOpen();
        }

        return () => {
            // When the dialog state tries to close, call the parent callback to also update its state.
            if (isOpen && !dialogProps.isOpen && onClose) {
                onClose();
            }
        };
    }, [dialogProps.isOpen, handleOpen, isOpen, onClose]);

    return <UIAddToMainNavigationDialog onClose={dialogOnClose} {...dialogProps} />;
};

export default AddToMainNavigationDialog;
